import { Injectable } from '@angular/core';
import { BehaviorSubject, filter, firstValueFrom, Observable } from 'rxjs';
import { License, ClientLicense, Product } from '@mira-avant/mtlibs';
import { ApiService } from './api.service';

@Injectable({
	providedIn: 'root',
})
export class LicenseService {
	private readonly BASE_PATH = 'licenses';
	public selectedClientLicenseBs$ = new BehaviorSubject<ClientLicense | undefined>(undefined);
	public readonly selectedClientLicense$ = this.selectedClientLicenseBs$.asObservable();
	public loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public clientLicensesBs$: BehaviorSubject<ClientLicense[]> = new BehaviorSubject<
		ClientLicense[]
	>([]);
	public clientLicenses$: Observable<ClientLicense[]> = this.clientLicensesBs$.pipe(
		filter(clientLicenses => clientLicenses !== null && clientLicenses !== undefined)
	);

	constructor(private api: ApiService) {
		// No initial load, as client licenses require a client_uuid
	}

	public selectClientLicense(clientLicense: ClientLicense | undefined): void {
		this.selectedClientLicenseBs$.next(clientLicense);
	}

	private async withLoading<T>(operation: () => Promise<T>): Promise<T> {
		this.loading$.next(true);
		try {
			return await operation();
		} finally {
			this.loading$.next(false);
		}
	}

	public async getLicenseById$(licenseId: string): Promise<ClientLicense | undefined> {
		const clientLicenses = await firstValueFrom(this.clientLicenses$);
		return clientLicenses.find(license => license.license_id === licenseId);
	}

	public async getLicenseTypes$(): Promise<License[]> {
		return this.withLoading(async () => {
			const licenses = await firstValueFrom(this.api.get<License[]>(this.BASE_PATH));
			return licenses;
		});
	}

	public async getClientLicenses$(client_uuid: string): Promise<ClientLicense[]> {
		return this.withLoading(async () => {
			const clientLicenses = await firstValueFrom(
				this.api.get<ClientLicense[]>(`${this.BASE_PATH}/client/${client_uuid}`)
			);
			this.clientLicensesBs$.next(clientLicenses);
			return clientLicenses;
		});
	}

	public async getActiveProducts$(): Promise<Product[]> {
		return this.withLoading(async () => {
			const activeProducts = await firstValueFrom(
				this.api.get<Product[]>(`${this.BASE_PATH}/active_products`)
			);
			return activeProducts;
		});
	}
}
